/**
 * Targeting Workspace Drawer
 *
 * Right slideout drawer (Ctrl+Shift+W) showing analyst workspace:
 * - Priority requests (from mission planners)
 * - Pinned items (analyst favorites)
 * - Recently worked (auto-tracked)
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTargetingWorkspace } from '../hooks/useTargetingWorkspace';

const COLORS = {
  background: '#070a0d',
  panel: '#0c1117',
  panelBorder: '#1a2533',
  primary: '#00d4ff',
  success: '#00ff88',
  warning: '#ffaa00',
  danger: '#ff4444',
  text: '#e6f0ff',
  textDim: '#5a7090',
};

const ENTITY_ICONS = {
  site: '🏢',
  facility: '🏭',
  dmpi: '⌖',
};

const ENTITY_TYPE_DISPLAY = {
  site: 'Installation',
  facility: 'Facility',
  dmpi: 'Aim Point',
};

export default function TargetingWorkspaceDrawer({ isOpen, onToggle, analystId, onSelectEntity, onOpenInbox, unreadCount }) {
  const {
    workspace,
    loading,
    error,
    togglePin,
    acknowledgePriority,
  } = useTargetingWorkspace(analystId);

  /**
   * Format relative timestamp
   */
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  /**
   * Format lineage breadcrumbs
   */
  const formatLineage = (item) => {
    const parts = [];
    if (item.site_name) parts.push(item.site_name);
    if (item.facility_name) parts.push(item.facility_name);
    if (item.dmpi_name && item.entity_type === 'dmpi') parts.push(item.dmpi_name);
    return parts.length > 0 ? parts.join(' → ') : item.entity_name || 'Unknown';
  };

  /**
   * Get entity icon
   */
  const getEntityIcon = (entityType) => {
    return ENTITY_ICONS[entityType] || '📍';
  };

  /**
   * Handle entity selection
   */
  const handleSelectEntity = (item) => {
    if (onSelectEntity) {
      onSelectEntity(item);
    }
  };

  /**
   * Render workspace item
   */
  const WorkspaceItem = ({ item, section }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <div
        onClick={() => handleSelectEntity(item)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${COLORS.panelBorder}`,
          cursor: 'pointer',
          background: isHovered ? COLORS.background : 'transparent',
          transition: 'background 0.15s',
          position: 'relative',
        }}
      >
        {/* Priority Badge */}
        {section === 'priority' && !item.priority_acknowledged && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              padding: '4px 8px',
              background: COLORS.danger,
              color: COLORS.background,
              borderRadius: '3px',
              fontSize: '10px',
              fontWeight: 700,
            }}
          >
            PRIORITY
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          {/* Entity Icon */}
          <div style={{ fontSize: '18px' }}>{getEntityIcon(item.entity_type)}</div>

          {/* Content */}
          <div style={{ flex: 1 }}>
            {/* Short Code + Name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              {item.short_code && (
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: COLORS.primary,
                  }}
                >
                  {item.short_code}
                </span>
              )}
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: COLORS.text,
                }}
              >
                {item.name || 'Unknown'}
              </span>
            </div>

            {/* Lineage */}
            <div
              style={{
                fontSize: '11px',
                color: COLORS.textDim,
                marginBottom: '6px',
              }}
            >
              {formatLineage(item)}
            </div>

            {/* Metadata */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                fontSize: '10px',
                color: COLORS.textDim,
              }}
            >
              {section === 'recent' && (
                <>
                  <span>{formatRelativeTime(item.last_accessed_at)}</span>
                  <span>•</span>
                  <span>{item.access_count} view{item.access_count !== 1 ? 's' : ''}</span>
                </>
              )}
              {section === 'priority' && item.priority_requested_by && (
                <span>From {item.priority_requested_by} · {formatRelativeTime(item.priority_requested_at)}</span>
              )}
            </div>

            {/* Priority briefing card (compact) */}
            {section === 'priority' && (item.critical_element || item.damage_guidance) && (
              <div style={{
                marginTop: '8px',
                padding: '8px 10px',
                background: `${COLORS.danger}0a`,
                border: `1px solid ${COLORS.danger}30`,
                borderRadius: '4px',
                fontSize: '11px',
              }}>
                {item.critical_element && (
                  <div style={{ marginBottom: item.damage_guidance ? '5px' : 0 }}>
                    <span style={{ color: COLORS.textDim }}>Critical Element: </span>
                    <span style={{ color: COLORS.text, fontWeight: 600 }}>{item.critical_element}</span>
                    {item.critical_element_location && (
                      <span style={{ color: COLORS.textDim }}> · {item.critical_element_location}</span>
                    )}
                  </div>
                )}
                {item.damage_guidance && (
                  <div>
                    <span style={{ color: COLORS.textDim }}>Guidance: </span>
                    <span style={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: item.damage_guidance === 'destroy' ? COLORS.danger : COLORS.warning,
                    }}>
                      {item.damage_guidance}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons (visible on hover) */}
            {isHovered && (
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  gap: '8px',
                }}
              >
                {/* Pin/Unpin */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin(item.id, item.pinned);
                  }}
                  style={{
                    padding: '4px 10px',
                    background: item.pinned ? COLORS.warning : COLORS.background,
                    border: `1px solid ${item.pinned ? COLORS.warning : COLORS.panelBorder}`,
                    borderRadius: '3px',
                    color: item.pinned ? COLORS.background : COLORS.textDim,
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: item.pinned ? 600 : 400,
                  }}
                >
                  {item.pinned ? '📌 Unpin' : '📌 Pin'}
                </button>

                {/* View Full Briefing in Inbox */}
                {section === 'priority' && onOpenInbox && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenInbox();
                    }}
                    style={{
                      padding: '4px 10px',
                      background: COLORS.primary,
                      border: `1px solid ${COLORS.primary}`,
                      borderRadius: '3px',
                      color: COLORS.background,
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    📥 View Briefing
                  </button>
                )}

                {/* Acknowledge Priority */}
                {section === 'priority' && !item.priority_acknowledged && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      acknowledgePriority(item.id);
                    }}
                    style={{
                      padding: '4px 10px',
                      background: COLORS.success,
                      border: `1px solid ${COLORS.success}`,
                      borderRadius: '3px',
                      color: COLORS.background,
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    ✓ Acknowledge
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  WorkspaceItem.propTypes = {
    item: PropTypes.object.isRequired,
    section: PropTypes.oneOf(['priority', 'pinned', 'recent']).isRequired,
  };

  /**
   * Get workspace sections
   */
  const sections = useMemo(() => {
    if (!workspace) return [];

    return [
      {
        id: 'priority',
        title: '🚨 High Priority Requests',
        items: workspace.priority_items || [],
        emptyMessage: 'No priority requests',
        color: COLORS.danger,
      },
      {
        id: 'pinned',
        title: '📌 Pinned Items',
        items: workspace.pinned_items || [],
        emptyMessage: 'No pinned items',
        color: COLORS.warning,
      },
      {
        id: 'recent',
        title: '🕒 Recently Worked',
        items: workspace.recent_items || [],
        emptyMessage: 'No recent activity',
        color: COLORS.textDim,
      },
    ];
  }, [workspace]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={onToggle}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '450px',
          maxWidth: '100vw',
          background: COLORS.panel,
          borderLeft: `1px solid ${COLORS.panelBorder}`,
          boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.5)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${COLORS.panelBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            background: COLORS.panel,
            zIndex: 10,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 700,
                color: COLORS.primary,
                letterSpacing: '0.5px',
              }}
            >
              TARGETING WORKSPACE
            </h2>
            {analystId && (
              <p
                style={{
                  margin: '4px 0 0 0',
                  fontSize: '11px',
                  color: COLORS.textDim,
                }}
              >
                {analystId}
              </p>
            )}
          </div>
          <button
            onClick={onToggle}
            style={{
              background: 'transparent',
              border: 'none',
              color: COLORS.textDim,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div
            style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: COLORS.textDim,
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <div>Loading workspace...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: COLORS.danger,
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>❌</div>
            <div>{error}</div>
          </div>
        )}

        {/* No Analyst */}
        {!loading && !error && !analystId && (
          <div
            style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: COLORS.textDim,
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👤</div>
            <div>Please sign in to view your workspace</div>
          </div>
        )}

        {/* Workspace Sections */}
        {!loading && !error && analystId && workspace && (
          <>
            {sections.map((section) => (
              <div key={section.id}>
                {/* Section Header */}
                <div
                  style={{
                    padding: '16px 24px 12px',
                    background:
                      section.id === 'priority' ? `${COLORS.danger}10` : 'transparent',
                    borderBottom: `1px solid ${COLORS.panelBorder}`,
                    position: 'sticky',
                    top: '85px',
                    zIndex: 5,
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: section.color,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{section.title}</span>
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        background: COLORS.background,
                        borderRadius: '10px',
                        fontWeight: 600,
                      }}
                    >
                      {section.items.length}
                    </span>
                  </div>
                </div>

                {/* Section Items */}
                {section.items.length === 0 ? (
                  <div
                    style={{
                      padding: '24px',
                      textAlign: 'center',
                      color: COLORS.textDim,
                      fontSize: '12px',
                    }}
                  >
                    {section.emptyMessage}
                  </div>
                ) : (
                  section.items.map((item) => (
                    <WorkspaceItem key={item.id} item={item} section={section.id} />
                  ))
                )}
              </div>
            ))}
          </>
        )}

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${COLORS.panelBorder}`,
            marginTop: 'auto',
            background: COLORS.panel,
            fontSize: '10px',
            color: COLORS.textDim,
            textAlign: 'center',
            fontFamily: 'monospace',
          }}
        >
          Ctrl+Shift+W to toggle
        </div>
      </div>
    </>
  );
}

TargetingWorkspaceDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  analystId: PropTypes.string,
  onSelectEntity: PropTypes.func.isRequired,
  onOpenInbox: PropTypes.func,
  unreadCount: PropTypes.number,
};
